// Copyright 2020 TOKU Systems Inc.

import React, { useState, useEffect } from 'react';
import {
  Container,
  Select,
  MenuItem,
  Typography,
  Grid,
} from '@material-ui/core';
import AppBarWithMenu from '../app-bar-with-menu';
import moment from 'moment-timezone';
import illumass from '../../services/illumass';
import MaterialTable, { Column } from 'material-table';
import { useSnackbar } from 'notistack';
import * as _ from 'lodash';
import {
  Device,
  EntityRef,
  DeviceModel,
  DeviceModelSignalClass,
  SignalNumberFormatType,
} from '@equestria/spike';
import { useHistory } from 'react-router-dom';
import { List } from 'immutable';
import InfoDialog from '../info-dialog';

const timezone = moment.tz.guess();

class DeviceRecord {
  get serialNumber(): string {
    return this.device.serialNumber ?? '???';
  }

  get customerName(): string {
    return (this.device.customer as EntityRef)?.shortName ?? '';
  }

  get customerId(): string {
    return (this.device.customer as EntityRef)?.id ?? '';
  }

  get deviceModelId(): string {
    return (this.device.deviceModel as EntityRef)?.id ?? '';
  }

  get id(): string {
    return this.device.id ?? '';
  }

  get idReference(): string {
    return 'device!' + this.id;
  }

  get slug(): string {
    return this.device.slug ?? '';
  }

  get slugReference(): string {
    return 'device.' + this.slug;
  }

  constructor(readonly device: Device) {}
}

function makeRenderForSignalClass(
  sc: DeviceModelSignalClass
): (r: DeviceRecord) => JSX.Element {
  let format: (x: number) => string;
  switch (sc.numberFormat?.formatType ?? SignalNumberFormatType.PRECISION) {
    case SignalNumberFormatType.FIXED: {
      const digits = sc.numberFormat?.digits ?? 2;
      format = (x: number): string => x.toFixed(digits);
      break;
    }

    case SignalNumberFormatType.DEFAULT:
    case SignalNumberFormatType.PRECISION: {
      const digits = sc.numberFormat?.digits ?? 5;
      format = (x: number): string => x.toPrecision(digits);
      break;
    }

    case SignalNumberFormatType.SIGNAL_NUMBER_FORMAT_TYPE_NOOP: {
      format = (x: number): string => x.toString();
      break;
    }
  }

  return function SignalClass(r: DeviceRecord): JSX.Element {
    const last = _.get(r.device.lastMeasurements, [sc.slug ?? '']);

    if (last === undefined) {
      return <Typography variant="body1">N/A</Typography>;
    } else {
      const lt = _.get(last, 'timestamp');
      const lm = _.get(last, ['last']);

      let ltDisplay: string;
      if (lt !== undefined) {
        ltDisplay = moment.utc(lt).tz(timezone).format('MMM D h:mm:ss a');
      } else {
        ltDisplay = '???';
      }

      const lmText: string = (() => {
        const quantityText = lm.value === undefined ? '???' : format(lm.value);
        const mu = lm.measuringUnit === undefined ? '' : lm.measuringUnit;
        const shortName = typeof mu === 'string' ? mu : mu.shortName ?? '';
        return shortName.length > 0
          ? quantityText + ' ' + shortName
          : quantityText;
      })();

      return (
        <Grid container direction="column">
          <Grid item>
            <Typography variant="body1">{lmText}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="caption">{ltDisplay}</Typography>
          </Grid>
        </Grid>
      );
    }
  };
}

export default function Devices(): JSX.Element {
  const [started, setStarted] = useState(false);
  const [backdropLevel, setBackdropLevel] = useState(0);
  const [records, setRecords] = useState<DeviceRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<DeviceRecord[]>([]);
  const [infoDeviceRecord, setInfoDeviceRecord] = useState<
    DeviceRecord | undefined
  >(undefined);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [deviceModelIdFilter, setDeviceModelIdFilter] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [deviceColumns, setDeviceColumns] = useState<Column<DeviceRecord>[]>(
    []
  );
  const history = useHistory();

  useEffect(() => {
    if (!started) {
      setStarted(true);
      setBackdropLevel(backdropLevel + 1);
      illumass.deviceModel
        .search({})
        .then((results) => {
          const m = results.hits.map((m) => m._source);
          setDeviceModels(m);
          const t1000 = m.find((m) => m.slug === 'toku-systems.t1000');
          setDeviceModelIdFilter(t1000?.id ?? '');
        })
        .then(() => illumass.device.search({}, { size: 10000 }))
        .then((results) => {
          setRecords(results.hits.map((hit) => new DeviceRecord(hit._source)));
          setBackdropLevel(Math.max(backdropLevel - 1, 0));
        })
        .catch((err) => {
          console.error(err);
          enqueueSnackbar(_.get(err, ['message'], 'Unable to list devices.'), {
            variant: 'error',
          });
          setBackdropLevel(Math.max(backdropLevel - 1, 0));
        });
    }
  }, [started, backdropLevel, enqueueSnackbar]);

  useEffect(() => {
    const columns: Column<DeviceRecord>[] = [
      {
        title: 'Serial number',
        field: 'serialNumber',
        editable: 'never',
        grouping: false,
        filtering: false,
      },
    ];

    const nextFilteredRecords = records.filter(
      (r) => r.deviceModelId === deviceModelIdFilter
    );

    const groups = _.groupBy(nextFilteredRecords, (r) => r.customerId);
    if (_.size(groups) > 1) {
      columns.push({
        title: 'Customer',
        field: 'customerName',
        editable: 'never',
        grouping: true,
        filtering: true,
      });
    }

    const model = deviceModels.find((m) => m.id === deviceModelIdFilter);
    if (model !== undefined) {
      const convertSignalClassToColumn = (
        sc: DeviceModelSignalClass
      ): Column<DeviceRecord> => {
        const slug = sc.slug ?? 'unknown';

        return {
          title: _.capitalize(slug.split('-').join(' ')),
          editable: 'never',
          field: `device.lastMeasurements.${slug}.last.value`,
          type: 'numeric',
          grouping: false,
          filtering: false,
          render: makeRenderForSignalClass(sc),
          sorting: true,
        };
      };

      const addSignalClassColumn = (signalClassSlug?: string): void => {
        if (
          model.signalClasses !== undefined &&
          signalClassSlug !== undefined
        ) {
          const sc = model.signalClasses[signalClassSlug];
          if (sc) {
            columns.push(convertSignalClassToColumn(sc));
          }
        }
      };

      if (model.signalClasses !== undefined) {
        // Add default first
        addSignalClassColumn(model.defaultSignalSlug);

        // Add choses model specific columns
        // In the future we should provide a list of checkboxes and make
        // this user configurable
        switch (model.slug) {
          case 'toku-systems.t1000':
            addSignalClassColumn('sensor-temperature');
            addSignalClassColumn('ambient-temperature');
            addSignalClassColumn('battery-voltage');
            addSignalClassColumn('signal-strength');
            addSignalClassColumn('network-status-code');
            break;
          case 'toku-systems.ipn4gii':
            addSignalClassColumn('battery-voltage');
            break;
          case 'toku-systems.mc-iii':
            addSignalClassColumn('ambient-temperature');
            addSignalClassColumn('battery-voltage');
            addSignalClassColumn('supply-voltage');
            break;
        }
      }
    }

    setDeviceColumns(columns);
    setFilteredRecords(nextFilteredRecords);
  }, [deviceModelIdFilter, records, deviceModels]);

  const handlePlotClick = (
    _event: unknown,
    target: DeviceRecord | DeviceRecord[]
  ): void => {
    if (_.isArray(target)) {
      // Show plot
      history.push('/plot', {
        originRefs: List<DeviceRecord>(target)
          .map((x) => `device!${x.device.id}`)
          .toArray(),
        title: target.map((x) => x.serialNumber).join(','),
      });
    } else {
      // Show plot
      history.push('/plot', {
        originRefs: List([`device!${target.device.id}`]).toArray(),
        title: target.serialNumber,
      });
    }
  };

  const handleOnDeviceModelIdFilterChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>
  ): void => {
    setDeviceModelIdFilter(event.target.value as string);
  };

  const handleInfoClick = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    row: DeviceRecord | DeviceRecord[]
  ): void => {
    if (_.isArray(row)) {
      setInfoDeviceRecord(row[0]);
    } else {
      setInfoDeviceRecord(row);
    }
  };

  const handleInfoClose = (): void => {
    setInfoDeviceRecord(undefined);
  };

  return (
    <React.Fragment>
      <AppBarWithMenu title="Devices" />
      <Container>
        <MaterialTable
          isLoading={backdropLevel > 0}
          title={
            <Select
              value={deviceModelIdFilter}
              style={{ minWidth: 150 }}
              onChange={handleOnDeviceModelIdFilterChange}
            >
              {deviceModels.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.modelNumber}
                </MenuItem>
              ))}
            </Select>
          }
          options={{
            actionsColumnIndex: deviceColumns.length,
            exportAllData: true,
            exportButton: true,
            grouping: true,
          }}
          localization={{
            body: {
              emptyDataSourceMessage: 'No devices',
            },
          }}
          columns={deviceColumns}
          data={filteredRecords}
          actions={[
            {
              icon: 'show_chart',
              tooltip: 'Plot',
              onClick: handlePlotClick,
            },
            {
              icon: 'info',
              tooltip: 'Info',
              onClick: handleInfoClick,
            },
          ]}
        />
      </Container>
      <InfoDialog
        onClose={handleInfoClose}
        name={infoDeviceRecord?.serialNumber ?? ''}
        idRef={infoDeviceRecord?.idReference ?? ''}
        slugRef={infoDeviceRecord?.slugReference ?? ''}
        open={infoDeviceRecord !== undefined}
      />
    </React.Fragment>
  );
}
